import { InternalApiEntityType } from '../../../api-internal';
import { useProgressStepper, useStep } from '@salvapp/ui-components';
import { useCallback } from 'react';

export type ScreenableFieldSetupStepType =
  | 'SCREENABLE_DATA'
  | 'GOODLIST'
  | 'INCLUDED_GROUPS'
  | 'EXCLUDED_GROUPS'
  | 'MATCHING_TYPE'
  | 'USABLE_DATA'
  | 'SELECTOR_CODE'
  | 'SUMMARY';

export interface ScreenableFieldSetupStep {
  type: ScreenableFieldSetupStepType;
  title: string;
  description?: string;
}

const screenableDataStep: ScreenableFieldSetupStep = {
  type: 'SCREENABLE_DATA',
  title: 'Screened fields',
  description: 'Your data that will be screened',
};

const goodlistStep: ScreenableFieldSetupStep = {
  type: 'GOODLIST',
  title: 'Goodlisting',
};

const segmentsStep: ScreenableFieldSetupStep = {
  type: 'INCLUDED_GROUPS',
  title: 'Segments',
  description: 'Segments to be included in screening',
};

const excludedSegmentsStep: ScreenableFieldSetupStep = {
  type: 'EXCLUDED_GROUPS',
  title: 'Excluded Segments',
  description: 'Segments to be excluded from screening ',
};

const matchingTypeStep: ScreenableFieldSetupStep = {
  type: 'MATCHING_TYPE',
  title: 'Matching Type',
};

const listSelectionStep: ScreenableFieldSetupStep = {
  type: 'USABLE_DATA',
  title: 'Screened against',
  description: 'Lists to screen your data against',
};

const selectorCodeStep: ScreenableFieldSetupStep = {
  type: 'SELECTOR_CODE',
  title: 'Screened against',
  description: 'List Groups to screen your data against',
};

const summaryStep: ScreenableFieldSetupStep = {
  type: 'SUMMARY',
  title: 'Summary',
  description: 'Summary of the Flow',
};

export const PersonScreenableFieldSetupSteps: ScreenableFieldSetupStep[] = [
  screenableDataStep,
  segmentsStep,
  excludedSegmentsStep,
  matchingTypeStep,
  listSelectionStep,
  selectorCodeStep,
  summaryStep,
] as const;

export const TransactionScreenableFieldSetupSteps: ScreenableFieldSetupStep[] = [
  screenableDataStep,
  goodlistStep,
  segmentsStep,
  excludedSegmentsStep,
  matchingTypeStep,
  listSelectionStep,
  selectorCodeStep,
  summaryStep,
] as const;

export const useScreenableFieldSetupNavigation = (entityType: InternalApiEntityType) => {
  let steps: readonly ScreenableFieldSetupStep[] = [];
  switch (entityType) {
    case InternalApiEntityType.PERSON:
      steps = PersonScreenableFieldSetupSteps;
      break;
    case InternalApiEntityType.TRANSACTION:
      steps = TransactionScreenableFieldSetupSteps;
      break;
  }

  const progressStepper = useProgressStepper({ steps: steps.length });

  const stepSetters = new Map();
  steps.forEach((step, index) => {
    stepSetters.set(index, useStep({ ordinal: index, setSteps: progressStepper.setSteps }));
  });

  const progressStepperSteps = progressStepper.steps;
  const nextStep = () => {
    stepSetters.get(progressStepper.activeStep).setStep({ type: 'filled' });
    progressStepper.goToNextStep();
  };
  const previousStep = progressStepper.goToPrevStep;
  const activeStepIndex = progressStepper.activeStep - 1;
  const activeStep = steps[activeStepIndex];

  const setListCodeStepVisible = useCallback(
    (isVisible: boolean) => {
      const index = steps.findIndex((step) => step.type === 'SELECTOR_CODE');
      stepSetters.get(index + 1).setStep({ isHidden: !isVisible });
    },
    [steps]
  );

  return [
    steps,
    activeStep,
    activeStepIndex,
    progressStepperSteps,
    nextStep,
    previousStep,
    setListCodeStepVisible,
  ] as const;
};
